import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalMetadata } from '../utils/providers';
import { useLinkedRecordPanel } from './LinkedRecordPanel';
import { useOpenRecordInPanel, useSidePanel } from './SidePanel/SidePanelContext';
import {
  buildRecordPath,
  isLegacyRecordParam,
  parseOverlayTarget,
  parseRecordTarget,
  presentationFor,
  serializeParams,
  withRecordTarget,
  withoutRecordTarget,
} from '../utils/recordTargets';

/**
 * Turns the record target in the URL into an open presentation (REG-592).
 *
 * Mounted once, above the routed content. It reads `useLocation`, so it runs on
 * first load, after a login redirect, on client-side navigation, and on browser
 * Back/Forward — the previous mount-only effect only handled the first of those.
 */
function RecordTargetResolver(): null {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: metadata } = useGlobalMetadata();
  const { openLinkedRecord, dismissLinkedRecord } = useLinkedRecordPanel();
  const openInPanel = useOpenRecordInPanel();
  const { setInnerTab } = useSidePanel();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = parseRecordTarget(params);

    // A modal riding on top of a page detail lives in the overlay key; the
    // page's own target stays in `record` and resolves below as usual.
    const overlay = parseOverlayTarget(params);
    if (overlay) {
      openLinkedRecord(overlay.tableName, overlay.id);
    }

    if (!target) {
      if (!overlay) dismissLinkedRecord();
      return;
    }

    const routeName = metadata?.routeData?.tableToRouteName?.[target.tableName] || target.tableName;
    const presentation = presentationFor(target.tableName);

    if (presentation === 'panel') {
      const tableLabel = metadata?.tableData?.byName?.[target.tableName]?.ui_label
        || target.tableName.replace(/_/g, ' ');
      openInPanel({
        id: target.id,
        table: routeName,
        tableName: target.tableName,
        label: `${tableLabel} #${target.id}`,
      });
      if (target.tab) setInnerTab(target.tab);
      dismissLinkedRecord();

      // The panel holds several records at once, so no single one belongs in
      // the address bar. A merchant target is a one-shot instruction: consume
      // it and leave a clean URL behind.
      const rest = serializeParams(withoutRecordTarget(params));
      navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
      return;
    }

    // Rewrite pre-REG-592 links (?panel=, ?funder=, ?rep=) to the canonical
    // form so anything copied from here on is the shape we support.
    if (isLegacyRecordParam(params)) {
      const canonical = serializeParams(withRecordTarget(params, target));
      navigate(`${location.pathname}?${canonical}`, { replace: true });
      return;
    }

    if (presentation === 'page') {
      // A full-page detail only renders on its own route. An overlay modal on
      // top of it is already open above — don't dismiss it here.
      if (!overlay) dismissLinkedRecord();
      if (location.pathname !== `/${routeName}`) {
        navigate(buildRecordPath(routeName, target, params), { replace: true });
      }
      return;
    }

    if (!overlay) openLinkedRecord(target.tableName, target.id);
  }, [
    location.pathname, location.search, metadata,
    navigate, openInPanel, openLinkedRecord, dismissLinkedRecord, setInnerTab,
  ]);

  return null;
}

export default RecordTargetResolver;
